import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './auth/AuthContext.tsx'
import { AppShell } from './components/AppShell.tsx'
import { LoginPage } from './pages/LoginPage.tsx'
import { DashboardPage } from './pages/DashboardPage.tsx'
import { PlaceholderPage } from './pages/PlaceholderPage.tsx'
import { CatalogPage } from './pages/CatalogPage.tsx'
import { MachinePage } from './pages/MachinePage.tsx'
import { NewMachinePage } from './pages/NewMachinePage.tsx'
import { ImportPage } from './pages/ImportPage.tsx'
import { ClientsPage } from './pages/ClientsPage.tsx'
import { ClientPage } from './pages/ClientPage.tsx'
import { QuotesPage } from './pages/QuotesPage.tsx'
import { QuotePage } from './pages/QuotePage.tsx'
import { QuoteEditorPage } from './pages/QuoteEditorPage.tsx'
import { OrdersPage } from './pages/OrdersPage.tsx'
import { OrderPage } from './pages/OrderPage.tsx'
import { SupplierOrderPage } from './pages/SupplierOrderPage.tsx'
import { WarehousePage } from './pages/WarehousePage.tsx'
import { UnitPage } from './pages/UnitPage.tsx'
import { ServicePage } from './pages/ServicePage.tsx'
import { ServiceTicketPage } from './pages/ServiceTicketPage.tsx'
import { TasksPage } from './pages/TasksPage.tsx'
import { ReportsPage } from './pages/ReportsPage.tsx'
import { SettingsPage } from './pages/SettingsPage.tsx'
import { GlobalSearch } from './components/GlobalSearch.tsx'
import { navFor } from './nav.ts'

export function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Ładowanie…
      </div>
    )
  }

  if (!user) return <LoginPage />

  const isOwner = user.role === 'wlasciciel'
  // Menu items whose screens aren't built yet still render an honest
  // "coming in stage N" page, so the shell is walkable end to end.
  const built = new Set(['/', '/katalog', '/klienci', '/oferty', '/zamowienia', '/magazyn', '/serwis', '/zadania', '/raporty', '/ustawienia'])
  const pending = navFor(user).filter((i) => !built.has(i.path))

  return (
    <AppShell>
      {/* Ctrl+K from anywhere (SPEC 5.7) */}
      <GlobalSearch />
      <Routes>
        <Route path="/" element={<DashboardPage />} />

        {/* Stage 2 — catalogue */}
        <Route path="/katalog" element={<CatalogPage />} />
        {/* Import writes purchase prices, so it's owner-only. Sales typing the
            URL falls through to the redirect below rather than seeing a 403. */}
        {isOwner && <Route path="/katalog/import" element={<ImportPage />} />}
        {/* Must sit before /katalog/:id, or "nowy" would be read as an id. */}
        {isOwner && <Route path="/katalog/nowy" element={<NewMachinePage />} />}
        <Route path="/katalog/:id" element={<MachinePage />} />

        {/* Stage 3 — clients */}
        <Route path="/klienci" element={<ClientsPage />} />
        <Route path="/klienci/:id" element={<ClientPage />} />

        {/* Stage 4 — offers */}
        <Route path="/oferty" element={<QuotesPage />} />
        <Route path="/oferty/:id" element={<QuotePage />} />
        <Route path="/oferty/:id/edytuj" element={<QuoteEditorPage />} />

        {/* Stage 5 — orders */}
        <Route path="/zamowienia" element={<OrdersPage />} />
        <Route path="/zamowienia/:id" element={<OrderPage />} />
        <Route path="/zamowienia-producent/:id" element={<SupplierOrderPage />} />

        {/* Stage 6 — warehouse */}
        <Route path="/magazyn" element={<WarehousePage />} />
        <Route path="/magazyn/:id" element={<UnitPage />} />

        {/* Stage 7 — service, tasks */}
        <Route path="/serwis" element={<ServicePage />} />
        <Route path="/serwis/:id" element={<ServiceTicketPage />} />
        <Route path="/zadania" element={<TasksPage />} />

        {/* Stage 9 — reports. Owner-only: the API returns 403 for anyone else,
            and sales never sees the menu item (nav.ts ownerOnly). A hand-typed
            URL falls through to the redirect below. */}
        {isOwner && <Route path="/raporty" element={<ReportsPage />} />}

        {/* Stage 12 — settings (owner-only): user management + company/mailbox. */}
        {isOwner && <Route path="/ustawienia" element={<SettingsPage />} />}

        {pending.map((i) => (
          <Route key={i.path} path={i.path} element={<PlaceholderPage title={i.label} stage={i.stage} />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}
